import { useMutation } from "@apollo/client";
import { useMergeLink } from "@mergeapi/react-merge-link";
import { useCallback, useEffect, useState } from "react";

import { errorNotificationVar, successNotificationVar } from "@cache/cache";

import {
  ExchangeMergePublicTokenMutation,
  ExchangeMergePublicTokenMutationVariables,
} from "../../../../types/graphql-schema";
import exchangeMergePublicTokenMutation from "../../graphql/exchange-merge-public-token";

interface UseMergeIntegrationLinkOptions {
  onSuccess: () => void;
  successMessage?: string;
}

interface UseMergeIntegrationLinkReturn {
  /**
   * Start the Merge link flow with the given token and origin ID.
   * Call this after getting a link token from createMergeLinkToken or relinkMergeDevIntegration.
   */
  startLink: (linkToken: string, endUserOriginId: string) => void;
  /** Whether a link flow is currently in progress */
  isLinking: boolean;
}

/**
 * Hook that encapsulates the Merge.dev link flow for both new connections and re-links.
 * Handles the link token state, public token exchange, and auto-opening the Merge Link modal.
 */
export function useMergeIntegrationLink({
  onSuccess,
  successMessage = "Integration connected successfully",
}: UseMergeIntegrationLinkOptions): UseMergeIntegrationLinkReturn {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [endUserOriginId, setEndUserOriginId] = useState<string | null>(null);

  const [exchangeMergePublicToken] = useMutation<
    ExchangeMergePublicTokenMutation,
    ExchangeMergePublicTokenMutationVariables
  >(exchangeMergePublicTokenMutation);

  const handleMergeLinkSuccess = useCallback(
    async (publicToken: string) => {
      if (!endUserOriginId) {
        errorNotificationVar({
          title: "Error",
          description: "No endUserOriginId available",
        });
        return;
      }

      try {
        const result = await exchangeMergePublicToken({
          variables: {
            publicToken,
            endUserOriginId,
          },
        });

        if (result.data?.exchangeMergePublicToken?.success) {
          successNotificationVar({
            title: "Success",
            description: successMessage,
          });
          setEndUserOriginId(null);
          onSuccess();
        } else {
          errorNotificationVar({
            title: "Error",
            description:
              result.data?.exchangeMergePublicToken?.error ||
              "Failed to connect integration",
          });
        }
      } catch (error) {
        errorNotificationVar({
          title: "Error",
          description:
            error instanceof Error
              ? error.message
              : "Failed to connect integration",
        });
      }
    },
    [exchangeMergePublicToken, endUserOriginId, successMessage, onSuccess],
  );

  const { open: openMergeLink, isReady: mergeLinkReady } = useMergeLink({
    linkToken: linkToken || undefined,
    onSuccess: handleMergeLinkSuccess,
    onExit: () => {
      setLinkToken(null);
    },
  });

  // Auto-open MergeLink when token is set and ready
  useEffect(() => {
    if (linkToken && mergeLinkReady) {
      openMergeLink();
    }
  }, [linkToken, mergeLinkReady, openMergeLink]);

  const startLink = useCallback(
    (newLinkToken: string, newEndUserOriginId: string) => {
      setLinkToken(newLinkToken);
      setEndUserOriginId(newEndUserOriginId);
    },
    [],
  );

  return {
    startLink,
    isLinking: linkToken !== null,
  };
}
